import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Divider,
  Grid,
  Link,
  Snackbar,
  Typography,
  useTheme,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { useTranslation } from 'react-i18next';
import { pascalCase } from 'change-case';
import { unwrapResult } from '@reduxjs/toolkit';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  PartnerEnrollmentStatus, PartnerEnrollType, PartnerSource,
} from '../../../../common/enums';
import { UnenrollmentDialog } from './UnenrollmentDialog';
import { ContactSalesDialog } from './ContactSalesDialog';
import { ImInterestedDialog } from './ImInterestedDialog';
import { PartnerContactSection } from './PartnerContactSection';
import { PartnerBenefitsSection } from './PartnerBenefitsSection';
import { PartnerIntroSection } from './PartnerIntroSection';
import { PartnerOverviewSection } from './PartnerOverviewSection';
import { PartnerHighlightsSection } from './PartnerHighlightsSection';
import { PartnerOffersSection } from './PartnerOffersSection';
import { PartnerRebateMatrixSection } from './PartnerRebateMatrixSection';
import { PartnerRelatedArticlesSection } from './PartnerRelatedArticlesSection';
import { PartnerResourcesSection } from './PartnerResourcesSection';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import {
  MarkPartnerSalesContacted,
  resetCompanyPartnerOptin,
  selectCompanyPartnerOptin,
  selectContactedSalesPartnerIds,
  selectCreateCompanyPartnerOptinState,
  selectGetCompanyPartnerOptinState,
  selectGetCompanyPartnerRelations,
} from '../../../../store/slices/dashboardSlice';
import {
  usePartnerExternalEnrollmentForms,
  usePartnerSpecificModalConfigMap,
} from '../Enrollment/PartnerSpecificFormConfig';
import { useServiceNationUrl } from '../../../../common/utils/useServiceNationUrl';
import {
  canMemberUnlinkPartnerEnrollment,
  getPaidPartnerEnrollmentStatus,
  getPartnerEnrollmentStatus,
  homeDepotPartnerId,
} from '../../../../common/utils/PartnerEnrollmentUtils';
import { selectMemberInformation } from '../../../../store/slices/onboardingSlice';
import {
  createCompanyPartnerOptin,
  enrollCompanyToPartner,
  getCompanyPartnerOptin,
  getCompanyPartnerRelations,
} from '../../../../store/thunks/dashboardThunk';
import { isPaidTier } from '../../../../common/utils/MembershipTierAccess';
import { useNavigationUtils } from '../../../../common/utils/useNavigationUtils';
import { useCustomEventTracking } from '../../../../common/useCustomEventTracking';
import OptimusButton from '../../../../common/library/Button/Button';
import { TestIds } from '../../../../assets/testIds';
import {
  COAST_CODE,
  getBasePartnerCode,
  getCanonicalPartnerName,
  HD_SUPPLY_CODE,
  SPARTAN_TOOL_CODE,
  SUNBELT_RENTALS_CODE,
  WESUPPLYTRADES_CODE,
  WINSUPPLY_CODE,
} from '../../../../common/constants';
import { useUpgradeNavigation } from '../../../../common/hooks/useUpgradeNavigation';
import * as dashboardAPI from '../../../../api/dashboardAPI';

const ACTION_REQUIRED_PROFILE_CTA_PARTNERS = new Set<string>([
  COAST_CODE,
  HD_SUPPLY_CODE,
  SPARTAN_TOOL_CODE,
  SUNBELT_RENTALS_CODE,
  WESUPPLYTRADES_CODE,
  WINSUPPLY_CODE,
]);

const MARK_AS_COMPLETE_PARTNERS = new Set<string>([
  COAST_CODE,
  HD_SUPPLY_CODE,
  SPARTAN_TOOL_CODE,
  SUNBELT_RENTALS_CODE,
  WESUPPLYTRADES_CODE,
  WINSUPPLY_CODE,
]);

const PROFILE_ACTION_BUTTON_TEXT_KEYS: Partial<Record<string, string>> = {
  [HD_SUPPLY_CODE]: 'partnerDetails.createHdSupplyAccount',
  [SPARTAN_TOOL_CODE]: 'partnerDetails.createSpartanToolAccount',
  [SUNBELT_RENTALS_CODE]: 'partnerDetails.createSunbeltRentalsAccount',
  [WESUPPLYTRADES_CODE]: 'partnerDetails.createAccount',
};

type PartnerDetailsProps = {
  partnerId: string;
  partnerName?: string;
  partnerCode?: string;
  partnerSource?: PartnerSource;
  isLocked?: boolean;
  requiresSalesContact?: boolean;
};

export const PartnerDetails = ({
  partnerId,
  partnerName,
  partnerCode,
  partnerSource,
  isLocked = false,
  requiresSalesContact = false,
}: PartnerDetailsProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [openUnlink, setOpenUnlink] = useState(false);
  const [openContactSales, setOpenContactSales] = useState(false);
  const [contactSalesSource, setContactSalesSource] = useState<'profile' | 'rebateMatrix'>('profile');
  const [openImInterested, setOpenImInterested] = useState(false);
  const [isCompletingRequiredAction, setIsCompletingRequiredAction] = useState(false);
  const [requiredActionError, setRequiredActionError] = useState(false);
  const { company } = useAppSelector(selectMemberInformation);
  // Kept in the (persisted) store rather than local state so the disabled CTA survives a
  // reload and stays in sync with the partner's card on the partners page (SN-874).
  const contactedSalesPartnerIds = useAppSelector(selectContactedSalesPartnerIds);
  const hasContactedSales = contactedSalesPartnerIds.includes(partnerId);
  const companyPartnerRelations = useAppSelector(selectGetCompanyPartnerRelations);
  const companyPartnerOptin = useAppSelector(selectCompanyPartnerOptin);
  const createOptinState = useAppSelector(selectCreateCompanyPartnerOptinState);
  const getOptinState = useAppSelector(selectGetCompanyPartnerOptinState);
  const externalEnrollmentForms = usePartnerExternalEnrollmentForms();
  const partnerSpecificModalConfigMap = usePartnerSpecificModalConfigMap();
  const { openExternalLink } = useNavigationUtils();
  const { buildServiceNationUrl } = useServiceNationUrl();
  const { trackPartnerEnrollment, trackEngagement } = useCustomEventTracking();
  const { navigateToUpgrade } = useUpgradeNavigation();

  // Rename-stable name for deriving analytics event names (see getCanonicalPartnerName).
  const partnerKeyName = getCanonicalPartnerName(partnerCode, partnerName);
  const basePartnerCode = getBasePartnerCode(partnerCode ?? '');

  const subscriptionTier = company?.subscription?.subscriptionTier;
  // Reads the shared paid/free chart rather than testing against Edge inline: the two
  // agree today, but "paid" is a product decision that already moved once (Association),
  // and `isPaidTier` is the one place it is recorded (SN-996).
  const isPaidSubscription = isPaidTier(subscriptionTier);
  const isPartnerLocked = isLocked ||
    (!isPaidSubscription && partnerSource === PartnerSource.ServiceNation);

  const getCompanyPartnerRelation = useCallback(
    (localPartnerId: string) => companyPartnerRelations?.find(
      (cp) => cp?.partnerId === localPartnerId,
    ),
    [companyPartnerRelations],
  );
  const enrollmentStatus = getPartnerEnrollmentStatus(
    getCompanyPartnerRelation(partnerId),
  );

  const externalEnrollmentFormConfig = externalEnrollmentForms[basePartnerCode];
  const externalEnrollmentForm = externalEnrollmentFormConfig?.externalEnrollmentForm;
  const externalEnrollmentSsoDestination = externalEnrollmentFormConfig?.ssoDestination;
  const partnerProfileCtaConfig =
    ACTION_REQUIRED_PROFILE_CTA_PARTNERS.has(basePartnerCode) ?
      partnerSpecificModalConfigMap[basePartnerCode] :
      undefined;
  const partnerProfileCtaUrl = partnerProfileCtaConfig &&
    !externalEnrollmentForm &&
    !externalEnrollmentSsoDestination ?
    partnerProfileCtaConfig.confirmationBtnLink :
    undefined;
  let paidEnrollmentStatus: PartnerEnrollmentStatus | undefined;
  if (isPaidSubscription) {
    // WeSupplyTrades account creation follows its Add Rewards questionnaire, so it must not
    // make a not-started enrollment look like an up-front required form.
    paidEnrollmentStatus = getPaidPartnerEnrollmentStatus(
      enrollmentStatus,
      !!(externalEnrollmentForm || externalEnrollmentSsoDestination),
      !!companyPartnerOptin,
    );
  }
  const effectiveEnrollmentStatus = paidEnrollmentStatus ?? enrollmentStatus;
  // Which paid CTA to show depends on the opt-in, so there is no action to render until the
  // fetch settles. Without flagging that as loading, renderPartnerAction returns null and
  // PartnerOverviewSection falls back to the Edge "Add rewards" badge for a frame — a CTA a
  // paid member could click into the wrong enrollment flow.
  const isPaidOptinLoading = isPaidSubscription &&
    (getOptinState === 'pending' || createOptinState === 'pending');

  useEffect(() => {
    if (company?.id) {
      dispatch(resetCompanyPartnerOptin());
      dispatch(getCompanyPartnerRelations(company.id));
      dispatch(getCompanyPartnerOptin({ companyId: company.id, partnerId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, partnerId]);

  const handleImInterestedClick = () => {
    trackPartnerEnrollment('Click', `${pascalCase(partnerKeyName)}ImInterested`);
    setOpenImInterested(true);
  };

  // The opt-in row this writes is what puts the member on the partner's next scheduled
  // report email (event-service SendReportsToPartners), so confirming here is the send.
  const handleImInterestedConfirm = () => {
    if (!company?.id) return;
    trackPartnerEnrollment('Submit', `${pascalCase(partnerKeyName)}ImInterested`);
    setOpenImInterested(false);
    dispatch(createCompanyPartnerOptin({ companyId: company.id, partnerId }));
  };

  const handleCompleteRequiredForm = async () => {
    trackPartnerEnrollment('Click', `${pascalCase(partnerKeyName)}RequiredForm`);
    if (externalEnrollmentSsoDestination) {
      try {
        openExternalLink(await buildServiceNationUrl(externalEnrollmentSsoDestination));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to generate Service Nation SSO token', error);
      }
    } else if (externalEnrollmentForm) {
      openExternalLink(externalEnrollmentForm);
    } else if (partnerProfileCtaUrl) {
      openExternalLink(partnerProfileCtaUrl);
    }
  };

  const handleMarkAsComplete = async () => {
    if (!company?.id || isCompletingRequiredAction) return;

    setIsCompletingRequiredAction(true);
    setRequiredActionError(false);
    try {
      await dispatch(enrollCompanyToPartner({
        companyId: company.id,
        partnerId,
        data: {
          type: PartnerEnrollType.DirectEnroll,
          enrollData: null,
        },
      })).then(unwrapResult);
      trackPartnerEnrollment('Complete', `${pascalCase(partnerKeyName)}RequiredAction`);
    } catch {
      setRequiredActionError(true);
    } finally {
      setIsCompletingRequiredAction(false);
    }
  };

  const handleContactSalesClick = () => {
    trackEngagement('Navigation', 'Click', 'ContactSalesProfile', { partnerName: partnerKeyName, isOptimizelyEvent: true });
    setContactSalesSource('profile');
    setOpenContactSales(true);
  };

  const handleRebateMatrixContactSalesClick = () => {
    trackEngagement('Navigation', 'Click', 'ContactSalesRebateMatrix', { partnerName: partnerKeyName, isOptimizelyEvent: true });
    setContactSalesSource('rebateMatrix');
    setOpenContactSales(true);
  };

  const handleContactSalesConfirm = async () => {
    if (!company?.id) return;
    const submitEvent = contactSalesSource === 'rebateMatrix' ?
      'ContactSalesRebateMatrixSubmit' :
      'ContactSalesProfile';
    trackEngagement('Navigation', 'Submit', submitEvent, { partnerName: partnerKeyName, isOptimizelyEvent: true });
    try {
      await dashboardAPI.requestContactSales(company.id, partnerId);
      setOpenContactSales(false);
      dispatch(MarkPartnerSalesContacted(partnerId));
    } catch (error) {
      // Keep the modal open so the member can retry.
      // eslint-disable-next-line no-console
      console.error('Failed to send contact sales request', error);
    }
  };

  const handleUpgradeNowClick = () => {
    trackEngagement('Navigation', 'Click', 'UpgradeNowProfile', { partnerName: partnerKeyName, isOptimizelyEvent: true });
    navigateToUpgrade();
  };

  const handleRebateMatrixUpgradeNowClick = () => {
    trackEngagement('Navigation', 'Click', 'UpgradeNowRebateMatrix', { partnerName: partnerKeyName, isOptimizelyEvent: true });
    navigateToUpgrade();
  };

  const renderPartnerAction = () => {
    // A required form replaces the enrollment call to action for a member who can already
    // reach the partner; it does not override the tier gate. The "Partners with a required
    // form" chart on product's "SN Logic on the Edge platform" page is explicit about this:
    // an Edge member sees Upgrade on Lennox Residential, and an Association or Roundtable
    // member sees Contact Sales on Rheem and Ruud. So this is evaluated below, after both
    // Contact Sales and Upgrade have had their say (SN-996).
    const hasExternalRequiredForm =
      !!(externalEnrollmentForm || externalEnrollmentSsoDestination) &&
      effectiveEnrollmentStatus !== PartnerEnrollmentStatus.Completed &&
      effectiveEnrollmentStatus !== PartnerEnrollmentStatus.InProgress;
    const hasPartnerProfileCta =
      effectiveEnrollmentStatus === PartnerEnrollmentStatus.ActionRequired &&
      ACTION_REQUIRED_PROFILE_CTA_PARTNERS.has(basePartnerCode);
    let actionButtonText =
      t('partnerDetails.completeRequiredForm', { defaultValue: 'Complete Required Form' });
    if (partnerProfileCtaUrl) {
      actionButtonText = partnerProfileCtaConfig?.confirmationBtnText || actionButtonText;
      const profileActionButtonTextKey = PROFILE_ACTION_BUTTON_TEXT_KEYS[basePartnerCode];
      if (profileActionButtonTextKey) {
        actionButtonText = t(profileActionButtonTextKey);
      }
    }
    if (requiresSalesContact) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1.5) }}>
          <OptimusButton
            data-testid={TestIds.partnerDetailsPage.contactSalesBtn}
            customVariant="primary"
            size="m"
            leadingIcon={<LockOutlinedIcon />}
            onClick={handleContactSalesClick}
            disabled={hasContactedSales}
            buttonText={t('partnerDetails.contactSales.cta') || 'Contact Sales'}
            fullWidth
          />
          {hasContactedSales && (
            <Typography
              variant="body8"
              color={theme.palette.grey[700]}
              data-testid={TestIds.partnerDetailsPage.contactSalesConfirmationMsg}
            >
              {t('partnerDetails.contactSales.confirmation')}
            </Typography>
          )}
        </Box>
      );
    }

    if (isPartnerLocked) {
      return (
        <OptimusButton
          data-testid={TestIds.partnerDetailsPage.upgradeNowBtn}
          customVariant="primary"
          size="m"
          leadingIcon={<LockOutlinedIcon />}
          onClick={handleUpgradeNowClick}
          buttonText={t('button.upgradeNow') || ''}
          fullWidth
        />
      );
    }

    // HD enrollment is fully managed by PartnerOverviewSection's HDEnrollmentStatusPanel.
    // Returning null here keeps overrideAction undefined so that panel renders unobstructed.
    if (partnerId === homeDepotPartnerId) return null;

    if (hasExternalRequiredForm || (hasPartnerProfileCta && partnerProfileCtaUrl)) {
      const requiredActionButton = (
        <OptimusButton
          size="m"
          customVariant="primary"
          fullWidth
          trailingIcon={<OpenInNewIcon />}
          onClick={handleCompleteRequiredForm}
          buttonText={actionButtonText}
          data-testid={TestIds.partnerDetailsPage.completeRequiredFormBtn}
        />
      );

      if (
        effectiveEnrollmentStatus !== PartnerEnrollmentStatus.ActionRequired ||
        !MARK_AS_COMPLETE_PARTNERS.has(basePartnerCode)
      ) {
        return requiredActionButton;
      }

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
          {requiredActionButton}
          <OptimusButton
            size="m"
            customVariant="secondaryGray"
            fullWidth
            leadingIcon={<TaskAltIcon />}
            onClick={handleMarkAsComplete}
            buttonText={t('partnerDetails.markAsComplete', {
              defaultValue: 'Mark as Complete',
            }) as string}
            data-testid={TestIds.partnerDetailsPage.markAsCompleteBtn}
          />
        </Box>
      );
    }

    if (!isPaidSubscription) return null;

    if (getOptinState === 'pending' || createOptinState === 'pending') return null;

    // Status badges and completed shopping actions are rendered by PartnerOverviewSection.
    if (paidEnrollmentStatus !== PartnerEnrollmentStatus.NotStarted) return null;

    return (
      <OptimusButton
        data-testid={TestIds.partnerDetailsPage.imInterestedBtn}
        customVariant="primary"
        size="m"
        leadingIcon={<InfoOutlinedIcon />}
        onClick={handleImInterestedClick}
        disabled={getOptinState === 'error'}
        buttonText={t('partnerDetails.imInterested.cta', {
          defaultValue: 'I’m Interested',
        }) as string}
        fullWidth
      />
    );
  };

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      <Snackbar
        open={createOptinState === 'error' || getOptinState === 'error' || requiredActionError}
        autoHideDuration={4000}
        onClose={(_, reason) => {
          if (reason !== 'clickaway') {
            setRequiredActionError(false);
            dispatch(resetCompanyPartnerOptin());
          }
        }}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          {t('error.generalMessage')}
        </Alert>
      </Snackbar>
      <Grid container>
        {/* left column */}
        <Grid item lg={4} md={4} sm={4} xs={12}>
          <Box sx={{
            display: 'flex',
            padding: `${theme.spacing(2)} ${theme.spacing(0)} ${theme.spacing(0)} ${theme.spacing(4)}`,
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: theme.spacing(2),
            alignSelf: 'stretch',
            [theme.breakpoints.down('md')]: {
              paddingLeft: theme.spacing(1),
            },
            [theme.breakpoints.down('sm')]: {
              paddingLeft: theme.spacing(0),
            },
          }}>
            <PartnerOverviewSection
              partnerId={partnerId}
              overrideAction={renderPartnerAction() ?? undefined}
              overrideEnrollmentStatus={paidEnrollmentStatus}
              isEnrollmentActionLoading={isCompletingRequiredAction || isPaidOptinLoading}
            />
            <PartnerHighlightsSection />
            <PartnerContactSection
              partnerName={partnerName || ''}
              partnerCode={partnerCode}
            />
            {canMemberUnlinkPartnerEnrollment(enrollmentStatus) &&
            <Typography variant='body8' color={theme.palette.grey[700]}>{t('partnerDetails.notInterestedQues')}
              {' '}
              <Link
                data-testid={TestIds.partnerDetailsPage.unlinkLnk}
                sx={{ cursor: 'pointer' }}
                onClick={() => setOpenUnlink(true)}
              >
                {t('partnerDetails.unlinkText')}
              </Link>
            </Typography>
            }
          </Box>
        </Grid>
        {/* right column */}
        <Grid item lg={8} md={8} sm={8} xs={12}>
          <Box
            sx={{
              display: 'flex',
              padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
              flexDirection: 'column',
              gap: theme.spacing(3),
              alignSelf: 'stretch',
              [theme.breakpoints.down('md')]: {
                padding: `${theme.spacing(2)} ${theme.spacing(0)} ${theme.spacing(2)} ${theme.spacing(2)}`,
              },
              [theme.breakpoints.down('sm')]: {
                padding: `${theme.spacing(2)} ${theme.spacing(0)} ${theme.spacing(2)} ${theme.spacing(1)}`,
              },
            }}>
            <PartnerIntroSection />
            <Divider />
            <PartnerOffersSection />
            <PartnerRebateMatrixSection
              onContactSales={handleRebateMatrixContactSalesClick}
              onUpgradeNow={handleRebateMatrixUpgradeNowClick}
              hasContactedSales={hasContactedSales}
              isPartnerLocked={isPartnerLocked}
            />
            <PartnerBenefitsSection />
            <PartnerResourcesSection
              partnerName={partnerName || ''}
              partnerCode={partnerCode}
            />
            <PartnerRelatedArticlesSection
              partnerName={partnerName || ''}
              partnerCode={partnerCode}
            />
          </Box>
        </Grid>
        <ContactSalesDialog
          open={openContactSales}
          onClose={() => setOpenContactSales(false)}
          onConfirm={handleContactSalesConfirm}
          partnerName={partnerName || ''}
          partnerCode={partnerCode}
        />
        <ImInterestedDialog
          open={openImInterested}
          onClose={() => setOpenImInterested(false)}
          onConfirm={handleImInterestedConfirm}
          partnerName={partnerName || ''}
          partnerCode={partnerCode}
        />
        {openUnlink && <UnenrollmentDialog
          partnerId={partnerId}
          updateDialogOpenState={setOpenUnlink}
          partnerName={partnerName || ''}
          partnerCode={partnerCode}
        />}
      </Grid >
    </>
  );
};
